/**
 * Generates and triggers download of a full order summary as an HTML file.
 * @param {Object} params
 * @param {string} params.orderName      - System order number (ORDNAME)
 * @param {string} params.orderReference - PO reference number
 * @param {Object} params.formData       - Order form data
 * @param {Array}  params.products       - Products list (for PARTDES lookup)
 * @param {Array}  params.deliveryDays   - Delivery days list (for date label)
 * @param {Array}  params.sites          - Delivery sites list (for site name)
 */
export function downloadOrderSummary({ orderName, orderReference, formData, products, deliveryDays, sites }) {
  const today = new Date().toLocaleDateString("he-IL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  // Resolve delivery date label
  const getDeliveryLabel = () => {
    if (!formData.dueDate) return "לא נבחר";
    const dateStr = formData.dueDate.split("T")[0];
    const match = deliveryDays?.find((d) => d.date === dateStr);
    if (match) return match.formatted;
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  // Resolve site label
  const getSiteLabel = () => {
    if (!formData.dcode) return null;
    return sites?.find((s) => s.CODE === formData.dcode)?.CODEDES || formData.dcode;
  };

  const siteLabel = getSiteLabel();
  const deliveryLabel = getDeliveryLabel();

  // Build items rows
  const itemRows = (formData.items || [])
    .map((item, index) => {
      const product = products?.find((p) => p.PARTNAME === item.partname);
      const partdes = product?.PARTDES || "-";
      return `
        <tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(item.partname)}</td>
          <td>${escapeHtml(partdes)}</td>
          <td>${item.quantity}</td>
        </tr>`;
    })
    .join("");

  // Optional sections
  const notesSection = formData.notes
    ? `<div class="section">
        <div class="section-title">הערות</div>
        <div class="notes-box">${escapeHtml(formData.notes)}</div>
       </div>`
    : "";

  const driverTextSection = formData.driverText
    ? `<div class="section">
        <div class="section-title">הוראות לנהג</div>
        <div class="notes-box">${escapeHtml(formData.driverText)}</div>
       </div>`
    : "";

  const siteRow = siteLabel
    ? `<tr><td class="label">אתר אספקה</td><td>${escapeHtml(siteLabel)}</td></tr>`
    : "";

  const referenceRow = formData.reference
    ? `<tr><td class="label">מספר הזמנת רכש</td><td>${escapeHtml(formData.reference)}</td></tr>`
    : "";

  const html = `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="UTF-8" />
  <title>אישור הזמנה - ${escapeHtml(orderName)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: Arial, sans-serif;
      direction: rtl;
      color: #222;
      background: #fff;
      padding: 32px;
      max-width: 800px;
      margin: 0 auto;
    }
    .header {
      border-bottom: 3px solid #1976d2;
      padding-bottom: 16px;
      margin-bottom: 24px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
    }
    .header-title { font-size: 22px; font-weight: 700; color: #1976d2; }
    .header-sub { font-size: 13px; color: #666; margin-top: 4px; }
    .header-date { font-size: 13px; color: #555; text-align: left; }
    .order-number-box {
      background: #e3f2fd;
      border: 2px solid #1976d2;
      border-radius: 8px;
      padding: 14px 20px;
      margin-bottom: 20px;
      text-align: center;
    }
    .order-number-label { font-size: 13px; color: #555; margin-bottom: 4px; }
    .order-number-value { font-size: 30px; font-weight: 700; color: #1976d2; letter-spacing: 1px; }
    .section { margin-bottom: 20px; }
    .section-title {
      font-size: 15px;
      font-weight: 700;
      color: #333;
      border-right: 4px solid #1976d2;
      padding-right: 8px;
      margin-bottom: 10px;
    }
    table.details { width: 100%; border-collapse: collapse; font-size: 14px; }
    table.details td { padding: 7px 10px; border: 1px solid #ddd; }
    table.details td.label { font-weight: 600; background: #f5f5f5; width: 35%; }
    table.items { width: 100%; border-collapse: collapse; font-size: 14px; }
    table.items thead tr { background: #1976d2; color: #fff; }
    table.items thead td { padding: 8px 10px; font-weight: 600; }
    table.items tbody tr:nth-child(even) { background: #f9f9f9; }
    table.items tbody td { padding: 7px 10px; border: 1px solid #ddd; }
    .notes-box {
      background: #f9f9f9;
      border-right: 4px solid #90caf9;
      padding: 10px 14px;
      font-size: 14px;
      white-space: pre-wrap;
      border-radius: 4px;
    }
    .footer {
      margin-top: 32px;
      border-top: 1px solid #ddd;
      padding-top: 12px;
      font-size: 12px;
      color: #888;
      text-align: center;
    }
    @media print {
      body { padding: 16px; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="header-title">אישור הזמנה - בית אבות</div>
      <div class="header-sub">Promedico</div>
    </div>
    <div class="header-date">
      תאריך יצירת הזמנה:<br/>
      <strong>${today}</strong>
    </div>
  </div>

  <div class="order-number-box">
    <div class="order-number-label">מספר הזמנה במערכת</div>
    <div class="order-number-value">${escapeHtml(orderName)}</div>
  </div>

  <div class="section">
    <div class="section-title">פרטי הזמנה</div>
    <table class="details">
      ${referenceRow}
      <tr><td class="label">צפי ליום הפצה</td><td>${escapeHtml(deliveryLabel)}</td></tr>
      ${siteRow}
    </table>
  </div>

  <div class="section">
    <div class="section-title">פריטים בהזמנה (${formData.items?.length || 0} פריטים)</div>
    <table class="items">
      <thead>
        <tr>
          <td style="width:40px">#</td>
          <td>מק&quot;ט</td>
          <td>תיאור מוצר</td>
          <td style="width:70px;text-align:center">כמות</td>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
      </tbody>
    </table>
  </div>

  ${notesSection}
  ${driverTextSection}

  <div class="footer">
    מסמך זה הופק אוטומטית ממערכת ההזמנות &nbsp;|&nbsp; טלפון: 1800-23-23-03 &nbsp;|&nbsp; CallCenterMosdi@Promedico.co.il
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const element = document.createElement("a");
  element.href = url;
  element.download = `הזמנה_${orderName}.html`;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  URL.revokeObjectURL(url);
}

function escapeHtml(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
